/**
 * AnnotationTools — die Lesewerkzeuge rund um Markierungen (#2267,
 * Designvorschlag #5186 Teil 5):
 *
 *   - Legende: alle Autoren mit Farbe und Anzahl; Klick blendet die
 *     anderen aus. Schalter für die Seitenverteilung der Karten.
 *   - Übersichtsleiste am rechten Rand: farbige Striche zeigen, wo im
 *     Dokument Markierungen liegen, das sichtbare Fenster ist markiert.
 *   - Panel „Markierungen": alle Einträge in Dokumentreihenfolge, als
 *     Markdown kopier- und speicherbar. Der Auszug, den man mitnimmt.
 *   - Tasten: J und K zur nächsten und vorigen Markierung, M markieren,
 *     A anmerken.
 *
 * Statische und Collab-Beiträge nutzen dieselben Bausteine; sie liefern
 * nur ihre Einträge (utils/annotationEntries).
 */

import { useEffect, useMemo, useState } from 'react'
import {
  anchorElement,
  entriesToMarkdown,
  entryAuthors,
  orderByDocument,
  type AnnotationEntry,
} from '../utils/annotationEntries'
import { colorOf, isAgentId } from '../utils/annotationAuthors'
import { filterEntries, goToEntry, type AnnotationToolsState } from '../hooks/useAnnotationTools'

/**
 * Blendet Markierungen aus, die der Filter nicht meint. Als Stylesheet
 * statt als Klasse am Element: Der Lesetext wird neu gerendert, sobald
 * sich der Beitrag ändert, eine gesetzte Klasse ginge dabei verloren.
 */
export function AnnotationFilterStyle({ hiddenIds }: { hiddenIds: Array<number | string> }) {
  if (hiddenIds.length === 0) return null
  const selector = hiddenIds
    .map((id) => `[data-annotation-id="${String(id).replace(/["\\]/g, '\\$&')}"]`)
    .join(',')
  return <style>{`${selector}{background:transparent !important;box-shadow:none !important;border-color:transparent !important;cursor:inherit;}`}</style>
}

// ---------------------------------------------------------------------------
// Legende
// ---------------------------------------------------------------------------

interface LegendProps {
  entries: AnnotationEntry[]
  tools: AnnotationToolsState
  /** Zwei Randspalten sind aktiv: dann ist die Verteilung wählbar. */
  twoSided: boolean
}

export function AnnotationLegend({ entries, tools, twoSided }: LegendProps) {
  // Nicht merken: Die zugewiesenen Farben laden nach dem ersten Rendern,
  // und die Liste ist klein.
  const authors = entryAuthors(entries)
  if (entries.length === 0) return null
  const notes = entries.filter((e) => e.kind === 'note').length
  const highlights = entries.length - notes
  return (
    <div className="annot-legend" role="toolbar" aria-label="Markierungen">
      <div className="annot-legend__authors">
        <button
          type="button"
          className={`annot-legend__chip${tools.author === null ? ' is-on' : ''}`}
          onClick={() => tools.setAuthor(null)}
          title="Alle Autoren zeigen"
        >
          Alle <span className="annot-legend__count">{entries.length}</span>
        </button>
        {authors.map((a) => (
          <button
            key={a.key}
            type="button"
            className={`annot-legend__chip${tools.author === a.key ? ' is-on' : ''}`}
            onClick={() => tools.setAuthor(tools.author === a.key ? null : a.key)}
            title={`Nur ${a.name} zeigen`}
            aria-pressed={tools.author === a.key}
          >
            <span className={`annot-legend__dot${a.agent ? ' annot-legend__dot--agent' : ''}`} style={{ background: a.color }} />
            {a.name} <span className="annot-legend__count">{a.count}</span>
          </button>
        ))}
      </div>
      <div className="annot-legend__tools">
        {twoSided && notes > 1 && (
          <button
            type="button"
            className="annot-legend__switch"
            onClick={() => tools.setSideMode(tools.sideMode === 'roles' ? 'space' : 'roles')}
            title={tools.sideMode === 'roles'
              ? 'Karten: Menschen links, Agenten rechts. Klick: nach Platz verteilen.'
              : 'Karten nach Platz verteilt. Klick: Menschen links, Agenten rechts.'}
            aria-pressed={tools.sideMode === 'roles'}
          >
            <i className={`fas ${tools.sideMode === 'roles' ? 'fa-people-arrows' : 'fa-columns'}`} />
            {tools.sideMode === 'roles' ? 'Menschen | Agenten' : 'nach Platz'}
          </button>
        )}
        <button
          type="button"
          className={`annot-legend__switch${tools.panelOpen ? ' is-on' : ''}`}
          onClick={() => tools.setPanelOpen(!tools.panelOpen)}
          title={`${notes} Anmerkungen, ${highlights} Markierungen. Tasten: J und K springen, M markiert, A merkt an.`}
          aria-pressed={tools.panelOpen}
        >
          <i className="fas fa-highlighter" /> Markierungen
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Übersichtsleiste
// ---------------------------------------------------------------------------

interface MinimapProps {
  root: HTMLElement | null
  entries: AnnotationEntry[]
}

interface MinimapGeometry {
  left: number
  top: number
  height: number
  ticks: Array<{ id: string; y: number; authorId?: string | null; authorName: string; highlight: boolean; title: string }>
  windowTop: number
  windowHeight: number
}

export function AnnotationMinimap({ root, entries }: MinimapProps) {
  const [geo, setGeo] = useState<MinimapGeometry | null>(null)

  useEffect(() => {
    if (!root || entries.length === 0) return
    const scroller = (root.closest('.workspace') as HTMLElement | null) || document.documentElement
    const measure = () => {
      const rect = scroller.getBoundingClientRect()
      const total = scroller.scrollHeight
      if (total <= 0 || rect.height <= 0) return
      const ticks: MinimapGeometry['ticks'] = []
      for (const e of entries) {
        const el = anchorElement(root, e.id)
        if (!el) continue
        const y = (el.getBoundingClientRect().top - rect.top + scroller.scrollTop) / total
        ticks.push({
          id: String(e.id),
          y: Math.min(1, Math.max(0, y)),
          authorId: e.author_id,
          authorName: e.author_name,
          highlight: e.kind === 'highlight',
          title: `${e.author_name}: ${(e.kind === 'note' ? e.note_text : e.quote).slice(0, 80)}`,
        })
      }
      setGeo({
        left: rect.right - 16,
        top: rect.top + 10,
        height: rect.height - 20,
        ticks,
        windowTop: scroller.scrollTop / total,
        windowHeight: Math.min(1, scroller.clientHeight / total),
      })
    }
    const raf = window.requestAnimationFrame(measure)
    scroller.addEventListener('scroll', measure, { passive: true })
    window.addEventListener('resize', measure)
    const observer = new ResizeObserver(measure)
    observer.observe(root)
    return () => {
      window.cancelAnimationFrame(raf)
      scroller.removeEventListener('scroll', measure)
      window.removeEventListener('resize', measure)
      observer.disconnect()
    }
  }, [root, entries])

  if (!geo || !root || entries.length === 0 || geo.ticks.length === 0) return null
  const jump = (ratio: number) => {
    const scroller = (root.closest('.workspace') as HTMLElement | null) || document.documentElement
    scroller.scrollTo({ top: ratio * scroller.scrollHeight - scroller.clientHeight / 2, behavior: 'smooth' })
  }
  return (
    <div
      className="annot-minimap"
      style={{ left: geo.left, top: geo.top, height: geo.height }}
      onClick={(ev) => {
        const box = ev.currentTarget.getBoundingClientRect()
        jump((ev.clientY - box.top) / box.height)
      }}
      aria-label="Übersicht der Markierungen"
    >
      <div className="annot-minimap__window" style={{ top: `${geo.windowTop * 100}%`, height: `${geo.windowHeight * 100}%` }} />
      {geo.ticks.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`annot-minimap__tick${t.highlight ? ' annot-minimap__tick--highlight' : ''}`}
          style={{ top: `${t.y * 100}%`, background: colorOf(t.authorId, t.authorName) }}
          title={t.title}
          onClick={(ev) => { ev.stopPropagation(); goToEntry(root, t.id) }}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Panel „Markierungen"
// ---------------------------------------------------------------------------

interface PanelProps {
  root: HTMLElement | null
  /** Alle Einträge; der Autor-Filter wird hier angewandt. */
  entries: AnnotationEntry[]
  tools: AnnotationToolsState
  title: string
  /** Eigenen Eintrag entfernen. Fehlt der Handler, gibt es den Knopf nicht. */
  onDelete?: (entry: AnnotationEntry) => Promise<boolean>
  /** Darf der Betrachter diesen Eintrag entfernen? */
  canDelete?: (entry: AnnotationEntry) => boolean
}

export function AnnotationPanel({ root, entries, tools, title, onDelete, canDelete }: PanelProps) {
  const [copied, setCopied] = useState(false)
  const authors = entryAuthors(entries)
  const shown = useMemo(
    () => orderByDocument(root, filterEntries(entries, tools.author)),
    // `root` ändert seinen Inhalt mit den Einträgen; die Reihenfolge wird
    // deshalb mit ihnen neu bestimmt.
    [root, entries, tools.author],
  )
  if (!tools.panelOpen) return null
  const markdown = () => entriesToMarkdown(title, shown)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(markdown())
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch { /* Zwischenablage gesperrt: Herunterladen bleibt */ }
  }
  const download = () => {
    const url = URL.createObjectURL(new Blob([markdown()], { type: 'text/markdown;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `markierungen-${title.toLowerCase().replace(/[^a-z0-9äöüß]+/gi, '-').replace(/^-|-$/g, '') || 'beitrag'}.md`
    a.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 2000)
  }
  return (
    <aside className="annot-panel" aria-label="Markierungen">
      <div className="annot-panel__bar">
        <span className="annot-panel__title"><i className="fas fa-highlighter" /> Markierungen</span>
        <span className="annot-panel__count">{shown.length}</span>
        <span className="annot-panel__spacer" />
        <button type="button" className="annot-panel__close" onClick={() => tools.setPanelOpen(false)} aria-label="Schließen" title="Schließen">
          <i className="fas fa-times" />
        </button>
      </div>
      <div className="annot-panel__filter">
        <select
          value={tools.author ?? ''}
          onChange={(e) => tools.setAuthor(e.target.value || null)}
          aria-label="Nach Autor filtern"
        >
          <option value="">Alle Autoren</option>
          {authors.map((a) => <option key={a.key} value={a.key}>{a.name} ({a.count})</option>)}
        </select>
        <button type="button" onClick={() => void copy()} title="Auszug als Markdown in die Zwischenablage">
          <i className="fas fa-copy" /> {copied ? 'Kopiert' : 'Kopieren'}
        </button>
        <button type="button" onClick={download} title="Auszug als Markdown-Datei speichern">
          <i className="fas fa-download" /> .md
        </button>
      </div>
      <div className="annot-panel__list">
        {shown.length === 0 && <div className="annot-panel__empty">Noch nichts markiert. Text auswählen und „Markieren“ oder „Anmerken“ wählen.</div>}
        {shown.map((e) => (
          <div
            key={String(e.id)}
            className={`annot-panel__item annot-panel__item--${e.kind}`}
            style={{ ['--annot-color' as string]: colorOf(e.author_id, e.author_name) }}
            onClick={() => goToEntry(root, e.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(ev) => { if (ev.key === 'Enter') goToEntry(root, e.id) }}
          >
            <div className="annot-panel__meta">
              <span className={`annot-legend__dot${isAgentId(e.author_id) ? ' annot-legend__dot--agent' : ''}`} style={{ background: colorOf(e.author_id, e.author_name) }} />
              <span className="annot-panel__author">{e.author_name}</span>
              <span className="annot-panel__kind">{e.kind === 'note' ? 'Anmerkung' : 'Markierung'}</span>
              {onDelete && canDelete?.(e) && (
                <button
                  type="button"
                  className="annot-panel__delete"
                  title="Entfernen"
                  aria-label="Entfernen"
                  onClick={(ev) => { ev.stopPropagation(); void onDelete(e) }}
                >
                  <i className="fas fa-trash" />
                </button>
              )}
            </div>
            {e.quote.trim() && <div className="annot-panel__quote">{e.quote.trim()}</div>}
            {e.kind === 'note' && <div className="annot-panel__note">{e.note_text}</div>}
            {e.replies.length > 0 && <div className="annot-panel__replies">{e.replies.length} {e.replies.length === 1 ? 'Antwort' : 'Antworten'}</div>}
          </div>
        ))}
      </div>
    </aside>
  )
}
